import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Check, X, Ruler, Wand2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const UnitsManager = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ code: "", aliases: "" });
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ code: "", aliases: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/units");
      setUnits(data.units);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const code = form.code.trim();
    if (!code) return;
    setBusy("add");
    try {
      await api.post("/admin/units", {
        code, name: code, aliases: form.aliases.split(",").map((a) => a.trim()).filter(Boolean),
      });
      setForm({ code: "", aliases: "" });
      toast.success(`“${code}” added`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const saveEdit = async (u) => {
    setBusy(u.code);
    try {
      const { data } = await api.patch("/admin/units", {
        code: u.code,
        rename_to: draft.code.trim() !== u.code ? draft.code.trim() : "",
        aliases: draft.aliases.split(",").map((a) => a.trim()).filter(Boolean),
      });
      toast.success("Unit updated", {
        description: data.items_updated ? `${data.items_updated} catalogue item(s) moved across` : undefined,
      });
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const remove = async (u) => {
    const warn = u.used
      ? `“${u.code}” is used on ${u.used} line(s)/item(s). Removing it stops Matiqs suggesting it — existing entries keep the label.\n\nRemove it?`
      : `Remove “${u.code}”?`;
    if (!window.confirm(warn)) return;
    setBusy(u.code);
    try {
      await api.delete(`/admin/units?code=${encodeURIComponent(u.code)}`);
      toast.success(`“${u.code}” removed`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const move = async (i, dir) => {
    const next = [...units];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setUnits(next);
    try {
      await api.put("/admin/units", { items: next.map((u) => u.code) });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const reclassify = async () => {
    if (!window.confirm("Run the universal units and trades over every catalogue and RFQ already in Matiqs?")) return;
    setBusy("reclassify");
    try {
      const { data } = await api.post("/admin/taxonomy/reclassify");
      toast.success("Tidy-up finished", {
        description: `${data.units_fixed} unit(s) standardised · ${data.trades_set} trade tag(s) set`,
        duration: 8000,
      });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-units" className="bg-card border border-border rounded-2xl p-6">
      <div className="flex flex-wrap items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
          <Ruler className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Universal units</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whatever a contractor or vendor types — “SQ.M”, “Sqm”, “m2” — lands on the same unit, so
            quotes compare line for line. Add the spellings your market uses as aliases.
          </p>
        </div>
        <Button variant="outline" data-testid="reclassify-button" onClick={reclassify} disabled={busy === "reclassify"} className="h-10 gap-2 font-semibold">
          {busy === "reclassify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Tidy existing data
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <Input data-testid="new-unit-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
               placeholder="Unit label — e.g. Cft" className="h-10 sm:w-48" />
        <Input data-testid="new-unit-aliases" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
               placeholder="Spellings to map, comma separated — c.ft, cu.feet, cubic feet" className="h-10 flex-1" />
        <Button data-testid="add-unit-button" onClick={add} disabled={busy === "add" || !form.code.trim()} className="h-10 gap-2 font-semibold shrink-0">
          {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </Button>
      </div>

      <div data-testid="unit-list" className="divide-y divide-border">
        {units.map((u, i) => (
          <div key={u.code} data-testid={`unit-row-${i}`} className="py-2.5">
            {editing === u.code ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input data-testid={`unit-edit-code-${i}`} value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className="h-9 sm:w-40" />
                <Input data-testid={`unit-edit-aliases-${i}`} value={draft.aliases} onChange={(e) => setDraft({ ...draft, aliases: e.target.value })} className="h-9 flex-1" />
                <div className="flex gap-1">
                  <Button size="sm" data-testid={`unit-save-${i}`} onClick={() => saveEdit(u)} disabled={busy === u.code} className="h-9 gap-1.5">
                    {busy === u.code ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-9"><X className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-muted-foreground w-6 shrink-0">{i + 1}</span>
                <span className="font-bold text-sm w-20 shrink-0">{u.code}</span>
                <span className="text-xs text-muted-foreground flex-1 truncate" title={(u.aliases || []).join(", ")}>
                  {u.name}{u.aliases?.length ? ` · ${u.aliases.slice(0, 6).join(", ")}${u.aliases.length > 6 ? "…" : ""}` : ""}
                </span>
                <span data-testid={`unit-used-${i}`} className={`px-2 py-0.5 rounded-md text-[11px] font-bold shrink-0 ${u.used ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
                  {u.used} in use
                </span>
                <div className="flex items-center shrink-0">
                  <Button size="icon" variant="ghost" data-testid={`unit-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="h-8 w-8"><ArrowUp className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`unit-down-${i}`} onClick={() => move(i, 1)} disabled={i === units.length - 1} className="h-8 w-8"><ArrowDown className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`unit-edit-${i}`} onClick={() => { setEditing(u.code); setDraft({ code: u.code, aliases: (u.aliases || []).join(", ") }); }} className="h-8 w-8"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`unit-delete-${i}`} onClick={() => remove(u)} disabled={busy === u.code} className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
